export interface CardField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multi-select' | 'tags';
  options?: string[];
  min?: number;
  max?: number;
  unit?: string;
}

export const CARD_ATTRIBUTE_SCHEMAS: Record<string, CardField[]> = {
  spell: [
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      options: [
        'fire',
        'ice',
        'wind',
        'healing',
        'light',
        'water',
        'dimensional',
        'time',
        'earth',
        'shadow',
        'nature',
        'arcane',
      ],
    },
    { key: 'tier', label: 'Tier', type: 'number', min: 1, max: 5 },
    { key: 'level_min', label: 'Min Level', type: 'number', min: 1, max: 100 },
    { key: 'level_max', label: 'Max Level', type: 'number', min: 1, max: 100 },
    {
      key: 'damage',
      label: 'Damage',
      type: 'select',
      options: ['none', 'low', 'avg', 'high', 'extreme'],
    },
    { key: 'range_meters', label: 'Range', type: 'number', min: 0, unit: 'm' },
    {
      key: 'effects',
      label: 'Effects',
      type: 'multi-select',
      options: [
        'burn',
        'freeze',
        'slow',
        'stun',
        'poison',
        'blind',
        'silence',
        'knockback',
        'bleed',
        'root',
        'fear',
        'heal',
        'shield',
        'haste',
      ],
    },
    { key: 'ether_cost', label: 'Ether Cost', type: 'number', min: 0 },
    { key: 'cast_time', label: 'Cast Time', type: 'text' },
    { key: 'cooldown', label: 'Cooldown', type: 'text' },
    { key: 'backlash', label: 'Backlash', type: 'text' },
    { key: 'evolutions', label: 'Evolutions', type: 'tags' },
  ],
  weapon: [
    {
      key: 'damage_type',
      label: 'Damage Type',
      type: 'select',
      options: [
        'slashing',
        'piercing',
        'blunt',
        'magical',
        'fire',
        'ice',
        'lightning',
      ],
    },
    {
      key: 'range',
      label: 'Range',
      type: 'select',
      options: ['melee', 'short', 'medium', 'long'],
    },
    {
      key: 'weight_class',
      label: 'Weight',
      type: 'select',
      options: ['light', 'medium', 'heavy'],
    },
    { key: 'material_tiers', label: 'Material Tiers', type: 'tags' },
    {
      key: 'required_level',
      label: 'Required Level',
      type: 'number',
      min: 1,
      max: 100,
    },
    {
      key: 'enchantable',
      label: 'Enchantable',
      type: 'select',
      options: ['yes', 'no'],
    },
  ],
  being: [
    {
      key: 'alignment',
      label: 'Alignment',
      type: 'select',
      options: [
        'lawful good',
        'neutral good',
        'chaotic good',
        'lawful neutral',
        'neutral',
        'chaotic neutral',
        'lawful evil',
        'neutral evil',
        'chaotic evil',
      ],
    },
    { key: 'avg_lifespan', label: 'Avg Lifespan', type: 'text' },
    { key: 'innate_abilities', label: 'Innate Abilities', type: 'tags' },
    { key: 'homeland', label: 'Homeland', type: 'text' },
    { key: 'weaknesses', label: 'Weaknesses', type: 'tags' },
  ],
  creature: [
    {
      key: 'danger_tier',
      label: 'Danger Tier',
      type: 'number',
      min: 1,
      max: 5,
    },
    { key: 'size_range', label: 'Size', type: 'text' },
    { key: 'lifespan', label: 'Lifespan', type: 'text' },
    {
      key: 'tameable',
      label: 'Tameable',
      type: 'select',
      options: ['yes', 'no', 'rare'],
    },
    { key: 'loot', label: 'Loot', type: 'tags' },
    { key: 'weaknesses', label: 'Weaknesses', type: 'tags' },
  ],
  potion: [
    { key: 'effect', label: 'Effect', type: 'text' },
    { key: 'duration', label: 'Duration', type: 'text' },
    {
      key: 'rarity',
      label: 'Rarity',
      type: 'select',
      options: ['common', 'uncommon', 'rare', 'epic', 'legendary'],
    },
    { key: 'ingredients', label: 'Ingredients', type: 'tags' },
    { key: 'side_effects', label: 'Side Effects', type: 'tags' },
  ],
  faction: [
    { key: 'alignment', label: 'Alignment', type: 'text' },
    { key: 'territory', label: 'Territory', type: 'text' },
    { key: 'specialization', label: 'Specialization', type: 'text' },
    { key: 'notable_members', label: 'Notable Members', type: 'tags' },
  ],
  magic_system: [
    { key: 'tiers', label: 'Tiers', type: 'text' },
    { key: 'levels', label: 'Levels', type: 'text' },
    { key: 'ether_pool_range', label: 'Ether Pool Range', type: 'text' },
    { key: 'restore_time', label: 'Restore Time', type: 'text' },
    { key: 'source_types', label: 'Source Types', type: 'tags' },
  ],
};

export const ELEMENT_ASSOCIATION_TYPES = [
  { value: 'has_ability', label: 'Has Ability' },
  { value: 'uses_weapon', label: 'Uses Weapon' },
  { value: 'knows_spell', label: 'Knows Spell' },
  { value: 'weak_to', label: 'Weak To' },
  { value: 'strong_against', label: 'Strong Against' },
  { value: 'inhabits', label: 'Inhabits' },
  { value: 'member_of', label: 'Member Of' },
  { value: 'allied_with', label: 'Allied With' },
  { value: 'enemy_of', label: 'Enemy Of' },
  { value: 'evolves_into', label: 'Evolves Into' },
  { value: 'requires', label: 'Requires' },
  { value: 'produces', label: 'Produces' },
  { value: 'related', label: 'Related' },
];
